import { NavGroup, NavItem, NavRow, NavSection } from '../model/nav-item';
import { MODULES } from './module-codes';
import { UserRole } from '../../data/enums/common-enum';

/**
 * SD-1218: the sidemenu, as data.
 *
 * Transcribed from the pre-SD-1218 sidebar-menu.component.html (lines 86-301). The ordering,
 * labels, icon basenames, tooltips and routerLinkActiveOptions are all exactly what that
 * template rendered -- nav-items.config.spec.ts asserts it item by item for every role.
 */

const ALL_ROLES = [UserRole.SuperAdmin, UserRole.Admin, UserRole.Submitter, UserRole.Custom];

/**
 * The previous `*ngIf="roleId != '3'"`. Note that it includes Custom: role 4 is declared in
 * the UserRole enum and handled nowhere, so `!= '3'` was true for it and a Custom user saw
 * Emails, Bills To Pay and Payments. Omitting it here would silently take three menu items
 * away from anyone on that role.
 */
const NOT_SUBMITTER = [UserRole.SuperAdmin, UserRole.Admin, UserRole.Custom];

/** The previous `*ngIf="roleId == '1'"`. */
const SUPER_ADMIN_ONLY = [UserRole.SuperAdmin];

/**
 * The groups, in order. Each is a row of its own: expanded it opens its children, collapsed its
 * icon stands for the whole group, which is why a section carries an icon now.
 */
export const NAV_SECTIONS: NavSection[] = [
  { id: 'inbox', label: 'Inbox', icon: 'Inbox' },
  { id: 'payables', label: 'Payables', icon: 'BillsToPay' },
  { id: 'sales', label: 'Sales', icon: 'Invoices' },
  // SD-1249
  { id: 'time', label: 'Time', icon: 'Time' },
  // Payments.svg is the bank building; Transactions.svg is the transfer arrows its first page
  // carries. The file names read backwards here, but the icons are right.
  { id: 'banking', label: 'Banking', icon: 'Payments' },
  { id: 'automation', label: 'Automation', icon: 'Flows' }
];

export const NAV_ITEMS: NavItem[] = [
  // SD-1248: Practice view is not in this menu, which is the selected organisation's. It opens from
  // the organisation switcher in the header, where a practice member finds it above their clients.
  {
    label: 'Home',
    icon: 'Dashboard',
    route: ['/dashboard'],
    placement: 'top',
    requiredModule: MODULES.HOME,
    requiredRoles: ALL_ROLES,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    label: 'Documents',
    icon: 'Inbox',
    route: ['/inbox'],
    section: 'inbox',
    placement: 'top',
    requiredModule: MODULES.INBOX,
    requiredRoles: ALL_ROLES,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    label: 'Emails',
    icon: 'Email',
    route: ['/emails'],
    section: 'inbox',
    placement: 'top',
    requiredModule: MODULES.EMAILS,
    requiredRoles: NOT_SUBMITTER,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    label: 'Bills To Pay',
    icon: 'BillsToPay',
    route: ['/payable/outstanding-amounts'],
    section: 'payables',
    placement: 'top',
    requiredModule: MODULES.BILLS_TO_PAY,
    requiredRoles: NOT_SUBMITTER,
    exact: true,
    clickHandler: 'payable',
    skeleton: true
  },
  {
    label: 'Payments',
    icon: 'Payments',
    route: ['/payable/payments'],
    section: 'payables',
    placement: 'top',
    requiredModule: MODULES.PAYMENTS,
    requiredRoles: NOT_SUBMITTER,
    clickHandler: 'payable',
    skeleton: true
  },
  {
    label: 'Invoices',
    icon: 'Invoices',
    route: ['/invoices'],
    section: 'sales',
    placement: 'top',
    requiredModule: MODULES.INVOICES,
    requiredRoles: SUPER_ADMIN_ONLY,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    // SD-1249: everyone logs their own time; the server's time.log permission is the boundary.
    label: 'Timesheet',
    icon: 'Time',
    route: ['/time'],
    section: 'time',
    placement: 'top',
    requiredModule: MODULES.TIME,
    requiredRoles: ALL_ROLES,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    /**
     * The bank/card ledger. NOT_SUBMITTER rather than ALL_ROLES, deliberately: a
     * submitter is sent to Paperwork to attach their own receipts, whereas this
     * screen is the organisation's whole statement including running balances.
     * The server's mask (11, seeded by 20260915_Transactions_Seed_Module.sql) is
     * authoritative; this is the fallback copy of it.
     */
    label: 'Transactions',
    icon: 'Transactions',
    route: ['/transactions'],
    section: 'banking',
    placement: 'top',
    requiredModule: MODULES.TRANSACTIONS,
    requiredRoles: NOT_SUBMITTER,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    label: 'Expense Reports',
    icon: 'Paperwork',
    route: ['/paperwork/missing-paperwork'],
    section: 'banking',
    placement: 'top',
    requiredModule: MODULES.PAPERWORK,
    requiredRoles: ALL_ROLES,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    label: 'Workflows',
    icon: 'Flows',
    route: ['/automate'],
    section: 'automation',
    placement: 'top',
    requiredModule: MODULES.FLOWS,
    requiredRoles: SUPER_ADMIN_ONLY,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    label: 'Agents',
    icon: 'Agents',
    route: ['/agents'],
    section: 'automation',
    placement: 'top',
    requiredModule: MODULES.AGENTS,
    requiredRoles: SUPER_ADMIN_ONLY,
    clickHandler: 'nav',
    skeleton: true
  },
  {
    label: 'Support',
    icon: 'contact_support',
    action: 'support',
    placement: 'bottom',
    requiredModule: MODULES.SUPPORT,
    requiredRoles: ALL_ROLES
  },
  {
    label: 'Settings',
    icon: 'settings',
    route: ['/setting/myprofile'],
    placement: 'bottom',
    requiredModule: MODULES.SETTINGS,
    requiredRoles: ALL_ROLES,
    clickHandler: 'nav'
  }
];

/**
 * The exact role -> module-code map the hardcoded sidebar produced before SD-1218.
 *
 * Used whenever the server cannot be trusted to answer: the entitlements feature flag is
 * off, the endpoint is unreachable, or it reported `degraded`. An empty sidebar is the one
 * catastrophic failure mode of this feature, so no failure path is allowed to produce one.
 */
export const LEGACY_FALLBACK: { [role: number]: string[] } = {
  [UserRole.SuperAdmin]: [
    MODULES.HOME, MODULES.INBOX, MODULES.EMAILS, MODULES.BILLS_TO_PAY, MODULES.PAYMENTS,
    MODULES.INVOICES, MODULES.TIME, MODULES.TRANSACTIONS, MODULES.PAPERWORK, MODULES.FLOWS, MODULES.AGENTS,
    MODULES.SUPPORT, MODULES.SETTINGS
  ],
  [UserRole.Admin]: [
    MODULES.HOME, MODULES.INBOX, MODULES.EMAILS, MODULES.BILLS_TO_PAY, MODULES.PAYMENTS,
    MODULES.TIME, MODULES.TRANSACTIONS, MODULES.PAPERWORK, MODULES.SUPPORT, MODULES.SETTINGS
  ],
  [UserRole.Submitter]: [
    // Emails is scoped server-side to the inboxes assigned to the Submitter.
    MODULES.HOME, MODULES.INBOX, MODULES.EMAILS, MODULES.TIME, MODULES.PAPERWORK, MODULES.SUPPORT, MODULES.SETTINGS
  ],
  [UserRole.Custom]: [
    MODULES.HOME, MODULES.INBOX, MODULES.EMAILS, MODULES.BILLS_TO_PAY, MODULES.PAYMENTS,
    MODULES.TIME, MODULES.TRANSACTIONS, MODULES.PAPERWORK, MODULES.SUPPORT, MODULES.SETTINGS
  ]
};

/** The codes a user with an unknown or missing role may see. Deliberately minimal, but
 *  never empty: Home and Settings keep them able to navigate and fix their account. */
export const MINIMAL_FALLBACK: string[] = [MODULES.HOME, MODULES.SUPPORT, MODULES.SETTINGS];

/** The fallback set for a role, tolerant of null/unknown roles. */
export function legacyFallbackFor(role: number | null | undefined): string[] {
  if (role === null || role === undefined) {
    return MINIMAL_FALLBACK;
  }
  return LEGACY_FALLBACK[role] || MINIMAL_FALLBACK;
}

/**
 * Build the rendered rows for a role and an entitlement set.
 *
 * A pure function on purpose: the parity spec calls it directly with literal inputs, which
 * is far more convincing than driving a TestBed and reading the DOM.
 *
 * A section header is emitted only when at least one of its children survived the filter.
 * Previously each header carried its own copy of a child's `*ngIf`, so revoking a module
 * could have left an orphan "Get Paid" heading with nothing under it.
 *
 * `applyRoleGate` decides whether NAV_ITEMS.requiredRoles -- this file's transcription of the
 * pre-SD-1218 `*ngIf` compares -- still filters on top of `modules`. It must stay on for a
 * LEGACY_FALLBACK set and must be off for a server-resolved one:
 *
 *   * Fallback: the caller has no server answer, so requiredRoles is the only role
 *     information there is. (It is in fact redundant, because legacyFallbackFor(role) is
 *     already per-role and every code it returns passes the gate for that role -- but it
 *     costs nothing and keeps the legacy path honest if the two tables ever drift.)
 *
 *   * Server-resolved: dbo.Module.AllowedRoleMask has already been applied, per module, per
 *     organisation, against the role the *server* resolved. Re-applying a frozen copy of the
 *     old compare here would veto exactly what the admin UI exists to change -- granting
 *     Emails to a Submitter would silently do nothing -- and would do it using a role the
 *     client may have cached from a different user entirely.
 */
export function buildRows(
  role: number | null | undefined,
  modules: string[],
  placement: 'top' | 'bottom' = 'top',
  items: NavItem[] = NAV_ITEMS,
  applyRoleGate: boolean = true,
  hasPractice: boolean = false
): NavRow[] {
  const visible = items.filter(item =>
    item.placement === placement && isNavItemVisible(item, role, modules, applyRoleGate, hasPractice)
  );

  const rows: NavRow[] = [];

  // Ungrouped items first -- Home sits above the first section header.
  visible
    .filter(item => !item.section)
    .forEach(item => rows.push({ kind: 'item', item }));

  NAV_SECTIONS.forEach(section => {
    const children = visible.filter(item => item.section === section.id);
    if (children.length === 0) {
      return;
    }

    rows.push({ kind: 'section', label: section.label, tooltip: section.tooltip });
    children.forEach(item => rows.push({ kind: 'item', item }));
  });

  return rows;
}

/**
 * The menu as the sidebar draws it: Home on its own, then a group per section.
 *
 * Same filter as buildRows -- it is the same `visible` list -- so what a role may see is decided
 * in one place; this only shapes it. A group with nothing left in it is dropped, so revoking a
 * module can never leave a group that opens onto nothing.
 */
export function buildNavGroups(
  role: number | null | undefined,
  modules: string[],
  placement: 'top' | 'bottom' = 'top',
  items: NavItem[] = NAV_ITEMS,
  applyRoleGate: boolean = true,
  hasPractice: boolean = false
): NavGroup[] {
  const visible = items.filter(item =>
    item.placement === placement && isNavItemVisible(item, role, modules, applyRoleGate, hasPractice)
  );

  const groups: NavGroup[] = visible
    .filter(item => !item.section)
    .map(item => ({ items: [item] }));

  NAV_SECTIONS.forEach(section => {
    const children = visible.filter(item => item.section === section.id);
    if (children.length === 0) {
      return;
    }
    groups.push({ section, items: children });
  });

  return groups;
}

/**
 * The loading skeleton, from the same config as the live menu. Section headers are shown
 * unconditionally here because at this point the role is not known yet, which is what the
 * previous hand-written skeleton block did too.
 */
export function buildSkeletonRows(items: NavItem[] = NAV_ITEMS): NavRow[] {
  const rows: NavRow[] = [];

  items
    .filter(item => item.placement === 'top' && !item.section && item.skeleton)
    .forEach(() => rows.push({ kind: 'placeholder' }));

  NAV_SECTIONS.forEach(section => {
    const children = items.filter(
      item => item.placement === 'top' && item.section === section.id && item.skeleton
    );
    if (children.length === 0) {
      return;
    }

    rows.push({ kind: 'section', label: section.label, tooltip: section.tooltip });
    children.forEach(() => rows.push({ kind: 'placeholder' }));
  });

  return rows;
}

/**
 * SD-1209: the single visibility test, shared by the sidemenu and the dashboard card
 * catalogue.
 *
 * Extracted from `buildRows` so a dashboard card cannot drift from the nav item for the same
 * module: a card is offered only if its module is one the caller could reach in the sidemenu.
 * It takes a structural type rather than `NavItem` so `DashboardCardDef` can be passed without
 * pretending to be a menu item.
 *
 * `applyRoleGate` carries the same meaning here as it does for `buildRows`, and for the same
 * reason -- see the long note above it. Pass `EntitlementState.fromFallback`: a server-resolved
 * set has already had `dbo.Module.AllowedRoleMask` applied, and re-filtering it on a frozen copy
 * of the old `*ngIf` compares would veto exactly what the Modules admin screen exists to change.
 */
export function isVisible(
  item: { requiredModule: string; requiredRoles?: number[] },
  role: number | null | undefined,
  modules: string[],
  applyRoleGate: boolean
): boolean {
  const entitled = modules || [];
  return entitled.indexOf(item.requiredModule) >= 0 &&
    (!applyRoleGate || roleAllows(item, role));
}

/**
 * SD-1248: the sidemenu's test. An item with `requires: 'practice'` shows exactly when the user
 * belongs to a practice; every other item is isVisible's business, unchanged.
 */
export function isNavItemVisible(
  item: NavItem,
  role: number | null | undefined,
  modules: string[],
  applyRoleGate: boolean,
  hasPractice: boolean
): boolean {
  if (item.requires === 'practice') {
    return hasPractice;
  }
  return isVisible(item, role, modules, applyRoleGate);
}

function roleAllows(
  item: { requiredRoles?: number[] },
  role: number | null | undefined
): boolean {
  if (!item.requiredRoles || item.requiredRoles.length === 0) {
    return true;
  }
  if (role === null || role === undefined) {
    return false;
  }
  return item.requiredRoles.indexOf(role) >= 0;
}

/** The active/inactive icon path for an item. Kept here so the template stays declarative. */
export function navIconPath(item: NavItem, isActive: boolean): string {
  return navIconSrc(item.icon, isActive);
}

/** The same, for a group's icon, which belongs to a section rather than an item. */
export function navIconSrc(icon: string, isActive: boolean): string {
  const folder = isActive ? 'blue-logos' : 'grey-logos';
  return 'assets/img/sidebar/' + folder + '/' + icon + '.svg';
}
